import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useIsWebView, useToken, useDoctorRouteGuard } from '../../../shared/hooks';
import { DoctorRouteDialog } from '../../../shared/components/DoctorRouteDialog';
import { appBridge } from '../../../shared/services/bridge';
import { useSignature } from '../hooks';
import { signatureRepository, type SignType } from '../data/signatureRepository';
import type { FeatureType, FromStatus } from '../types';

import './SignatureScreen.css';

const SignatureScreen: React.FC = () => {
  const navigate = useNavigate();
  const isWebView = useIsWebView();
  const { id } = useParams<{ id: string }>();
  const token = useToken();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const location = useLocation();
  const fromStatus = location.state?.fromStatus as FromStatus | undefined;

  // URLパスからfeatureTypeを判定
  const featureType: FeatureType = location.pathname.includes('/inspections/') ? 'inspection' : 'inquiry';

  // ドクタールート監視ガード（inquiry の場合のみ発動。inspection は対象外）
  const { shouldBlock, handleConfirm: handleGuardConfirm } = useDoctorRouteGuard(
    featureType === 'inquiry' ? id : undefined,
    token,
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isDrawingRef = useRef<boolean>(false);

  const {
    isAgreed,
    isSigned,
    canSubmit,
    setIsAgreed,
    setIsSigned,
    clearSignature,
    getSignatureConfig,
    getCompleteRoute,
  } = useSignature();

  const config = getSignatureConfig(featureType, fromStatus);

  // Canvasの初期化とリサイズ対応
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // 画面全体のスクロールを禁止
    document.body.style.overflow = 'hidden';

    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // マウス/タッチ位置を取得するヘルパー関数
  const getPos = (e: MouseEvent | TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  // 描画開始
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPos(e.nativeEvent as MouseEvent | TouchEvent);
    lastPosRef.current = pos;
    isDrawingRef.current = true;
    setIsSigned(true);
  };

  // 描画中
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const pos = getPos(e.nativeEvent as MouseEvent | TouchEvent);
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    lastPosRef.current = pos;
  };

  // 描画終了
  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDrawingRef.current = false;
  };

  // クリア処理
  const handleClearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    clearSignature();
  };

  /**
   * fromStatusからsignTypeを判定
   */
  const getSignType = (): SignType => {
    if (fromStatus === '作業報告書確認') {
      return 'complete';
    }
    // 調査見積もり済、またはその他の場合は見積書サイン
    return 'estimate';
  };

  // 保存処理
  const handleSaveSignature = async () => {
    if (!canvasRef.current || !id) return;

    const signature = canvasRef.current.toDataURL();

    // API呼び出し（Flutterオーバーレイローディングで2度押し防止）
    setIsSubmitting(true);
    setSubmitError(null);
    appBridge.showLoading();

    const signType = getSignType();
    const result = await signatureRepository.submitSignature(id, signType, signature, token);

    setIsSubmitting(false);

    if (!result.success) {
      appBridge.hideLoading();
      setSubmitError(result.error.message);
      return;
    }

    // 成功時は完了画面へ遷移（ローディングを解除してアニメーションを見せる）
    appBridge.hideLoading();
    const route = getCompleteRoute(featureType, id);
    navigate(route, { state: { fromStatus, featureType } });
  };

  // ドクタールートに切り替わっていた場合は Flutter ダッシュボードへ強制復帰
  if (shouldBlock) {
    return <DoctorRouteDialog onConfirm={handleGuardConfirm} />;
  }

  return (
    <div className="signature-container">
      <main className="signature-content">
        <h2 className="content-title">{config.title}</h2>
        <p className="signature-lead">{config.description}</p>
        <div className="signature-pad-wrapper">
          <canvas
            ref={canvasRef}
            className="signature-pad"
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseOut={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
          />
          {!isSigned && (
            <div className="signature-placeholder">
              <p className="signature-placeholder-text">{config.placeholderText}</p>
            </div>
          )}
        </div>

        <div className="clear-button-wrapper">
          <button className="clear-button" onClick={handleClearSignature}>
            クリア
          </button>
        </div>

        {config.consentParagraphs && (
          <div className="signature-consent">
            {config.consentParagraphs.map((paragraph, index) => (
              <p key={index} className="signature-consent-text">
                {paragraph}
              </p>
            ))}
          </div>
        )}

        <div className="agreement-checkbox-container">
          <input
            type="checkbox"
            id="agreement-checkbox"
            checked={isAgreed}
            onChange={(e) => setIsAgreed(e.target.checked)}
          />
          <label htmlFor="agreement-checkbox">
            {config.agreementText}
          </label>
        </div>

        {submitError && (
          <div className="signature-error">
            {submitError}
          </div>
        )}
      </main>

      <footer className="signature-footer">
        <button
          className="button-primary-signature"
          onClick={handleSaveSignature}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? '送信中...' : config.buttonText}
        </button>

        {!isWebView && (
          <button className="button-secondary-signature" onClick={() => navigate(-1)}>
            戻る
          </button>
        )}
      </footer>
    </div>
  );
};

export default SignatureScreen;
